/**
 * serviceUserPatientOverviewGating.js
 * ----------------------------------
 * Small business rules used by `ServiceUserPatientOverview`, importing only
 * pure utilities (referralStatus) so everything here stays zero-mock testable.
 *
 * Why this file exists:
 * - The page component pulls in Firebase + routing + UI, which makes unit tests noisy
 *   (and sometimes flaky) if we import the full page just to validate simple logic.
 * - By keeping the rules here, we can test them in isolation with zero mocks.
 */

import {
  BASE_REFERRAL_FORMS,
  ADHD_ONLY_REFERRAL_FORMS,
  hasStoredHfpInstrument,
} from '../utils/referralStatus';

/**
 * Decide whether to show the guardian "Provide additional information" (triage) panel.
 *
 * Business rule (plain English):
 * - Only guardians should see it.
 * - Only show it after the guardian has finished all required Referral + HFP forms
 *   (because triage is explicitly "additional" information).
 * - Do not show it if triage forms were already assigned (the UI switches to a
 *   confirmation panel instead).
 * - Do not show it for secondary-school referrals: they skip the HFP and are not
 *   asked to proceed with triage (TD-129 / secondary-school scope).
 *
 * @param {object} args
 * @param {'school_user'|'guardian'|null|undefined} args.viewerRoleSuffix
 * @param {boolean} args.triageFormsAssigned
 * @param {boolean} args.allFormsComplete
 * @param {'primary'|'secondary'|null|undefined} [args.schoolPhase]
 * @returns {boolean}
 */
export function shouldShowAssignTriageFormsPanel({
  viewerRoleSuffix,
  triageFormsAssigned,
  allFormsComplete,
  schoolPhase,
}) {
  return (
    viewerRoleSuffix === 'guardian' &&
    allFormsComplete === true &&
    triageFormsAssigned !== true &&
    schoolPhase !== 'secondary'
  );
}

/**
 * The ADHD-specific referral form (SNAP-IV). It is only presented once an ADHD
 * concern has been logged. Kebab key as used in the service-user progress map.
 */
export const ADHD_ONLY_REFERRAL_FORM_KEY = 'snap-iv-26';

/**
 * True when an ADHD concern has been logged by the school or the parent. The
 * intake concern is derived server-side from either referrer's reason for
 * referral: 'adhd' or 'both' mean ADHD is in scope. Autism-only ('asd') or an
 * unset concern (nothing logged yet) both stay false, so the ADHD SNAP-IV scale
 * stays hidden until an ADHD concern is actually logged.
 *
 * @param {string|null|undefined} intakeConcern - 'adhd' | 'asd' | 'both' | null
 * @returns {boolean}
 */
export function isAdhdConcern(intakeConcern) {
  return ['adhd', 'both'].includes(String(intakeConcern || '').trim().toLowerCase());
}

/**
 * The referral forms that count toward "all forms complete" for a referral.
 *
 * Business rule (plain English):
 * - Always the base referral forms.
 * - Include the ADHD-only SNAP-IV scale ONLY once an ADHD concern is logged
 *   (adhd/both); it stays out for autism-only and not-yet-logged referrals.
 * - Never the strengths and needs (HFP) forms: they belong to the SEND
 *   pathway, not the referral, at either school phase (Tom King, 27 October
 *   2026; TDI-495, TDI-511). A referral-only family is asked for the referral
 *   forms alone.
 *
 * @param {object} args
 * @param {string[]} args.referralFormKeys - base referral progress keys
 * @param {boolean} args.isAdhdConcern - whether an ADHD concern has been logged
 * @returns {string[]}
 */
export function requiredReferralCompletionKeys({
  referralFormKeys,
  isAdhdConcern: adhdConcern,
}) {
  return (referralFormKeys || []).filter(
    (k) => !(k === ADHD_ONLY_REFERRAL_FORM_KEY && adhdConcern !== true),
  );
}

/**
 * The backend (camelCase) form keys that indicate the OTHER party is "done".
 *
 * Business rule (plain English):
 * - Primary/unknown referrals: their HFP forms (the HFP report needs both
 *   parties, so their HFP completion is what "done" means).
 * - Their referral forms, at either school phase: once both parties finish
 *   their referral forms the CYP is ready for triage. The strengths and needs
 *   forms are the SEND pathway's, never the referral's (TDI-495, TDI-511);
 *   checking them here would leave the other party permanently incomplete
 *   and wrongly tell this user the other party still owes forms the referral
 *   never asked for.
 * - The ADHD SNAP-IV scale is only in the set once an ADHD concern is logged.
 *
 * Key lists come from referralStatus.js (the single source of truth for form
 * keys) rather than shadow copies, so a future form-set change cannot leave
 * this check judging completion against a stale set.
 *
 * @param {object} args
 * @param {boolean} args.isAdhdConcern
 * @returns {string[]}
 */
export function otherPartyRequiredFormKeys({ isAdhdConcern: adhdConcern }) {
  // The referral forms at either school phase: the strengths and needs
  // forms are the SEND pathway's, never the referral's (TDI-495, TDI-511), so
  // "the other party is done" means their referral forms are in.
  const referral = BASE_REFERRAL_FORMS.filter((k) => !ADHD_ONLY_REFERRAL_FORMS.includes(k));
  if (adhdConcern === true) referral.push(...ADHD_ONLY_REFERRAL_FORMS);
  return referral;
}

/**
 * Which optional form groups the service-user overview shows.
 *
 * Business rule (plain English):
 * - The HFP "Strength and Support Needs" group is hidden for secondary-school
 *   referrals (TD-129).
 * - The triage "Additional Information" group is guardian-only and only after
 *   triage forms have been assigned.
 *
 * @param {object} args
 * @param {boolean} args.isSecondaryReferral
 * @param {'school_user'|'guardian'|null|undefined} args.viewerRoleSuffix
 * @param {boolean} args.triageFormsAssigned
 * @returns {{ showStrengths: boolean, showTriage: boolean }}
 */
export function serviceUserFormGroupVisibility({
  isSecondaryReferral,
  viewerRoleSuffix,
  triageFormsAssigned,
}) {
  return {
    // The strengths and needs group never sits under a referral: those forms
    // are the SEND pathway's (TDI-495, TDI-511). A SEND-only Harbour pupil's
    // page is built separately by the caller.
    showStrengths: false,
    // Guardians see the Additional Information (triage) group once assigned.
    // On SECONDARY referrals it is always visible so guardians can see what
    // comes next; the timeline's sequential lock keeps the forms locked until
    // the referral forms are complete (assignment then happens automatically).
    showTriage:
      viewerRoleSuffix === 'guardian' &&
      (triageFormsAssigned === true || isSecondaryReferral === true),
  };
}

/**
 * Whether the record carries a generated HFP report the service user can open.
 *
 * Business rule (plain English):
 * - The HFP only exists once BOTH respondents have submitted, at which point the
 *   ingest writes a scored generation onto the record. Gate the Documents row on
 *   that presence so a not-yet-ready report is never offered.
 * - A record with a stored instrument (TDI-414) only offers its own
 *   generation's report, so a v4 record is never offered a v2/v3 profile.
 * - Records created before the setting keep the original rule: v3
 *   (domains.hfpV3.scoring) first, with v2 and v1 as fallbacks so legacy
 *   records still surface their profile.
 */
export function isHfpReportAvailable(record) {
  const domains = record?.domains || {};
  if (hasStoredHfpInstrument(record)) {
    return Boolean(domains[`hfp${record.hfpInstrument.toUpperCase()}`]?.scoring);
  }
  return Boolean(domains.hfpV3?.scoring || domains.hfpV2?.scoring || domains.hfp);
}

